/**
 * Pure: format an ISO date string for display, or a fallback when absent.
 *
 * Anchored to UTC for the same reason as `formatDateOnly` below: an ISO
 * timestamp at or near UTC midnight renders as the previous day for every
 * viewer west of UTC otherwise.
 */
export function formatDate(iso: string | null | undefined, fallback = '—'): string {
  if (!iso) return fallback;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/** Matches a bare calendar date, `YYYY-MM-DD`, with no time or zone component. */
const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Pure: format a date-only string (`YYYY-MM-DD`) for display, or a fallback
 * when absent or malformed.
 *
 * Separate from `formatDate` because a bare calendar date has no time zone.
 * `new Date('2026-09-30')` is read as UTC midnight, which renders as the 29th
 * for every viewer west of UTC - a target close date silently a day early.
 * This splits the string into its parts and formats them anchored to UTC, so
 * the day rendered is always the day stored, while the month/day/year order
 * still follows the viewer's locale like every other date in the app.
 */
export function formatDateOnly(date: string | null | undefined, fallback = '—'): string {
  const parts = date?.match(DATE_ONLY_PATTERN);
  if (!parts) return fallback;

  const [, year, month, day] = parts;
  const utc = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));

  // `Date.UTC` rolls out-of-range parts over rather than rejecting them
  // (month 13 becomes January of the next year), so a malformed date would
  // otherwise render as a confidently wrong real one. Round-trip instead.
  const roundTrips =
    utc.getUTCFullYear() === Number(year) &&
    utc.getUTCMonth() === Number(month) - 1 &&
    utc.getUTCDate() === Number(day);
  if (!roundTrips) return fallback;

  return utc.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Pure: format an ISO date string with a time-of-day component, or a
 * fallback when absent. Distinguishes repeat same-day events (e.g. multiple
 * profile views in one day) that `formatDate` alone would render as identical.
 */
export function formatDateTime(iso: string | null | undefined, fallback = '—'): string {
  if (!iso) return fallback;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}

const MS_PER_DAY = 86_400_000;

/**
 * Whole days from one instant to a later one, rounded UP, the same way the
 * Backend counts them in the reminder email, so the bell and the email always
 * agree. Never negative.
 *
 * @param fromIso - the earlier instant (ISO)
 * @param toIso - the later instant (ISO)
 * @returns days between them, or null when either is missing or invalid
 */
export function wholeDaysBetween(fromIso: string | null | undefined, toIso: string | null | undefined): number | null {
  if (!fromIso || !toIso) return null;
  const ms = new Date(toIso).getTime() - new Date(fromIso).getTime();
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.ceil(ms / MS_PER_DAY));
}

/**
 * "1 day" or "N days".
 *
 * @param days - a whole number of days
 * @returns the count with its unit
 */
export function dayCount(days: number): string {
  return `${days} ${days === 1 ? 'day' : 'days'}`;
}
